<script type="text/jsx">
import _ from 'lodash';
import { PBX_REGIONS } from '@/plans/ZOOM_PHONE/helper/constants';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover.vue';
import { getPlanUnitWithQuantity, planUnitMap } from '@/common/product-unit';
import { planTypes, trackingPlanTypes, ZOOM_ONE_PRO_PLUS_MAX_LICENSE } from '@/common/constants';
import { plansof } from '@/utils/chain';
import { mapMutations, mapGetters } from 'vuex';
import LicenseTip from '@/plans/ZOOM_MEETING/license-tip.vue';
import { getDownSellLicenseDesc, getDownSellExpireTime } from '@/utils/validate-downsell';

export default {
  components: { LicenseTip, InputNumberWithPopover },
  props: {
    region: {
      type: String,
      default: PBX_REGIONS.US_AND_CANADA,
    },
    planUnit: {
      type: Number,
      default: planUnitMap.user,
      validator(value) {
        return Object.values(planUnitMap).includes(value);
      },
    },
    maxHosts: {
      type: Number,
      default: 99,
    },
    minHosts: {
      type: Number,
      default: 1,
    },
    decreaseTips: String,
    getIncreaseTips: {
      type: Function,
      default: _.noop,
    },
    billingCycle: String,
    planList: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      license: 1,
    };
  },
  watch: {
    downgradeMinHost(val) {
      if (this.isSelectLargeMeetingWhenDowngrade && this.license < val) {
        this.changeLicense(val)
      }
    },
    license(val) {
      this.$emit('license-change', val)
    }
  },
  computed: {
    ...mapGetters(['getBoughtBasePlanInfo', 'assignInfo', 'migratePlanState', 'getSelectedPlans']),
    trackingPlanKey() {
      return trackingPlanTypes.ZONE_PRO_PLUS;
    },
    currentSelectedPlan() {
      return plansof(this.planList).cycle(this.billingCycle).get(0);
    },
    additionalSpans() {
      return {
        proplus: [
          {
            value: 1,
            text: '',
          },
          {
            value: this.maxHosts,
            text: this.$t('billing.zone.max_of_plans_user', [this.maxHosts, this.maxHosts + 1]),
          },
        ],
      }
    },
    isDowngrade() {
      return (
        this.$context.is.downgrade ||
        this.migratePlanState.is.downgrade
      );
    },
    currentAssignInfo() {
      const oldPlan = this.getBoughtBasePlanInfo?.plans?.[0];
      const { assignLicenseQuantity = 0, planExpireTime } = _.get(this.assignInfo, oldPlan.plan_code, {});
      return {
        assignLicenseQuantity,
        planExpireTime,
        isEffectiveImmediately: this.getBoughtBasePlanInfo.planType !== planTypes.ZOOM_ONE_BUNDLE || this.isSelectLargeMeetingWhenDowngrade,
      };
    },
    unAssignTips() {
      if (this.isDowngrade) {
        const oldPlan = this.getBoughtBasePlanInfo?.plans?.[0];
        const { assignLicenseQuantity = 0, planExpireTime } = this.currentAssignInfo;
        if (this.currentSelectedPlan) {
          return getDownSellLicenseDesc({
            oldPlan,
            nextPlan: this.currentSelectedPlan,
            quantity: this.license,
            assignLicenseQuantity,
            planExpireTime: getDownSellExpireTime(planExpireTime), 
          })?.errorMessage;
        }
      }
      return '';
    },
    downgradeMinHost() {
      if (this.isDowngrade && this.currentAssignInfo.isEffectiveImmediately) {
        return Math.max(this.currentAssignInfo.assignLicenseQuantity, this.minHosts);
      }
      return this.minHosts;
    },
    downgradeDecreaseTip() {
      if (this.isSelectLargeMeetingWhenDowngrade) {
        if (this.license <= this.currentAssignInfo.assignLicenseQuantity) {
          return this.$t('billing.licenses_assigned_limit_tips')
        }
      }
      return ''
    },
    isSelectLargeMeetingWhenDowngrade() {
      return this.isDowngrade && !!this.getSelectedPlans.find((p) => p.type === planTypes.LARGE_MEETING);
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    updateCart(newLicense, oldLicense) {
      const planCode = this.currentSelectedPlan?.plan_code;

      if (planCode) {
        this.updateSelectPlan({
          plan_code: planCode,
          cartInfo: { selected_number: newLicense },
        });
      }

      // event tracking
      planCode && this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} license`,
        `${this.trackingPlanKey} - ${newLicense > oldLicense ? 'increase' : 'decrease'} - ${newLicense}`);
    },
    changeLicense(newLicense) {
      const oldLicense = this.license;
      this.license = newLicense;
      this.updateShoppingCart(newLicense, oldLicense);
    },
    toggleLicenseTips(tips) {
      tips && this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} options`, 'licenses - tooltip');
    },
    handleLicenseTipsClick(e) {
      if(e?.target?.tagName === 'A') {
        this.$tracking.buyInteraction(`${this.trackingPlanKey} plan users`, 'the maximum amount is 99 users - contact sales');
      }
    },
    mx_renderLicense() {
      const planUnit = getPlanUnitWithQuantity(this.planUnit);

      return <div class="opc-bundle-config-row__input__number">
        {this.license <= ZOOM_ONE_PRO_PLUS_MAX_LICENSE && <license-tip class="opc-bundle-config-row__alert" type="proplus" number={this.license} additionalSpans={this.additionalSpans} onClick={this.handleLicenseTipsClick} />}
        <input-number-with-popover
          value={this.license}
          onChange={this.changeLicense}
          size="large"
          controls-position="both"
          min={this.downgradeMinHost}
          max={this.maxHosts}
          increaseTips={this.getIncreaseTips(this.selectCapacity, this.license)}
          decreaseTips={this.decreaseTips || this.downgradeDecreaseTip}
          unit={this.inputUnit}
          label={`${this.trackingPlanKey} + ' Select the number of ${planUnit}'`}
        />
        {
          this.unAssignTips && (
            <zm-alert class="reason-tips" type="info" closable={false}>
              <i class="zm-icon-info" style="color: #9191A4;"></i>
              <p domPropsInnerHTML={this.unAssignTips}></p>
            </zm-alert>
          )
        }
        {(this.$slots.licenseTips || !this.licenseTips) ?
          this.$slots.licenseTips :
          <zm-tooltip
            class="opc-bundle-config-row__tooltips"
            role="button"
            aria-label="Learn more about Select the number of licenses"
            content={this.licenseTips}
            placement="bottom"
            onInput={this.toggleLicenseTips}>
            <i class="zm-icon-info-outline" />
          </zm-tooltip>}
      </div>;
    },
    syncLicense() {
      if(!this.currentSelectedPlan) return;
      const cartLicense = this.currentSelectedPlan.cartInfo?.selected_number;
      if (cartLicense < this.downgradeMinHost) {
        this.license = this.downgradeMinHost;
      } else if (cartLicense > this.maxHosts) {
        this.license = this.maxHosts;
      } else {
        this.license = cartLicense;
      }
      if (this.license !== cartLicense) {
        this.updateSelectPlan({
          plan_code: this.currentSelectedPlan.plan_code,
          cartInfo: { selected_number: this.license },
        });
      }
    }
  },
  render() {
    return this.mx_renderLicense();
  },
  created() {
    if (this.currentSelectedPlan) {
      this.syncLicense();
    }

    // A closure (timeId) needs to be generated for each config row instance
    this.updateShoppingCart = _.debounce(this.updateCart, 200);
  },
};
</script>

<style lang="scss">
.opc-bundle-config-row {

  &__alert.is-warning {
    margin-bottom: 12px;
  }

  &__input__number {

  }
}
</style>
